import { ref } from 'vue';
import type {
  ClickData,
  ClickedCellAndFieldInfoForTransformed,
  ClickedCellInfoForTransformed,
} from '@/common/utils';
import type { Emits } from '../../modernGrid.setup';
import type { GridRowDataType, GridRawRowDataType } from '../../modernGrid.types';

export const useCellEvent = <RowDataType extends GridRawRowDataType>(emit: Emits<RowDataType>) => {
  const tooltipItems = ref<string[]>([]);
  const customTooltipRef = ref();

  const clickCell = (clickedInfo: ClickedCellInfoForTransformed<GridRowDataType<RowDataType>>) => {
    emit('click-cell', clickedInfo);
  };
  const clickCount = ({ evt, chips, totalChips, rowIndex }: ClickData) => {
    tooltipItems.value = totalChips;
    emit('click-cell-chip-more', { chips, totalChips, rowIndex });
    customTooltipRef.value?.show(evt);
  };
  const clickChip = ({ evt, chips, rowIndex }: ClickData) => {
    tooltipItems.value = chips;
    emit('click-cell-chip', { evt, chips, rowIndex });
    customTooltipRef.value?.show(evt);
  };
  const hideTooltip = () => {
    customTooltipRef.value?.hide();
  };

  const currentTooltipFieldName = ref<keyof GridRowDataType<RowDataType> | undefined>();
  const currentTooltipRowData = ref<GridRowDataType<RowDataType> | undefined>();
  const currentTooltipColumnIndex = ref(-1);
  const isChipCellClickable = ref(false);

  const clickCountWithFieldData = ({
    evt,
    chips,
    totalChips,
    rowIndex,
    fieldName,
    rowData,
    columnIndex,
    isClickable,
  }: ClickedCellAndFieldInfoForTransformed<GridRowDataType<RowDataType>>) => {
    tooltipItems.value = totalChips;
    currentTooltipFieldName.value = fieldName;
    currentTooltipRowData.value = rowData;
    currentTooltipColumnIndex.value = columnIndex || -1;
    isChipCellClickable.value = isClickable;
    emit('click-cell-chip-more', { chips, totalChips, rowIndex });
    customTooltipRef.value?.show(evt);
  };

  const onClickTooltipItem = (
    clickedInfo: ClickedCellInfoForTransformed<GridRowDataType<RowDataType>>,
  ) => {
    clickCell(clickedInfo);
    hideTooltip();
  };

  return {
    tooltipItems,
    customTooltipRef,
    currentTooltipFieldName,
    currentTooltipRowData,
    currentTooltipColumnIndex,
    isChipCellClickable,
    clickCell,
    clickCount,
    clickChip,
    hideTooltip,
    clickCountWithFieldData,
    onClickTooltipItem,
  };
};
